import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SlowMotionVideoRoundedIcon from '@mui/icons-material/SlowMotionVideoRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import { AppStoreBadge, GoogleBadge, Shape } from '../../../../assets/svg';
import {
  BetaTesterImg,
  FaceBookIcon,
  InstagramIcon,
  LandingIntroVideo,
  MessengerIcon,
  MojIcon,
  PatentPopup,
  Prelaunch,
  QuickieCoinPlayback,
  ShareChatIcon,
  SnapchatIcon,
  SummaryVideo,
  ThumbnailSummary,
  TikTokIcon,
  WhatsAppIcon,
  XIcon,
} from '../../../../assets/images';
import SpeedRamp from '../../../../assets/images/quickcam-speed-ramp-v2.png';
import { setLandingAppearanceLock } from '../../../../common/preferences/dashboard-preferences';
import { IReferrerData } from '../../../../common/services/user/user-data.interface';
import LandingReferrerWelcome from './landing-referrer-welcome.component';
import { GiftInvitePreview } from '../../../lifetime-credits/gift-invite-welcome';
import styles from './cinematic-homepage.module.scss';

// The original bespoke renders were removed with the untracked landing-page files.
// Keep the recovered design runnable with the closest preserved media asset.
const CinematicHero = ThumbnailSummary;

type CinematicHomepageProps = {
  isLoggedIn: boolean;
  referredBy?: IReferrerData;
  isLoadingReferrer?: boolean;
  getReferrerUser?: (channelId?: string, startTimer?: boolean) => void;
  onTryQuickCam: () => void;
  onLogin: () => void;
  onDashboard: () => void;
  giftInvite?: GiftInvitePreview | null;
};

type Speed = '0.25x' | '1x' | '4x';

type NavigationItem = {
  label: string;
  target?: string;
  href?: string;
};

const navigation: NavigationItem[] = [
  { label: 'Product', target: 'product' },
  { label: 'Features', target: 'features' },
  { label: 'Prelaunch', target: 'prelaunch' },
  { label: 'Ecosystem', target: 'ecosystem' },
  { label: 'VIP', target: 'vip' },
  { label: 'FAQs', target: 'faqs' },
  { label: 'Experience', target: 'experience' },
  { label: 'Whitepaper', href: '/whitepaper' },
];

const flowSteps = [
  {
    number: '1',
    title: 'Record',
    description: 'Live slow or fast motion as it happens.',
  },
  {
    number: '2',
    title: 'Edit',
    description: 'Trim, crop, add audio, and shape every beat.',
  },
  {
    number: '3',
    title: 'Share',
    description: 'Export once and publish wherever your audience lives.',
  },
];

const productFeatures = [
  {
    icon: SlowMotionVideoRoundedIcon,
    eyebrow: 'Camera',
    title: 'Multi-patented camera control.',
    description: 'Make the speed change part of the performance.',
    items: [
      'Fast and slow motion during live recording',
      'Zoom in and out during live recording',
      'All with one-touch control',
    ],
  },
  {
    icon: TuneRoundedIcon,
    eyebrow: 'Editor',
    title: 'Quickie Video Editor.',
    description: 'Finish without losing your creative momentum.',
    items: [
      'Quickly trim, cut, and crop your videos',
      'Add voice-over narration',
      'Protect your work with a creator watermark',
    ],
  },
  {
    icon: ShareRoundedIcon,
    eyebrow: 'Share',
    title: 'Social media sharing.',
    description:
      'Publish through the platforms where your audience already lives.',
    items: [
      'Share to TikTok, Instagram, Snapchat, X, and more',
      'Carry your finished Quickie into one connected social flow',
    ],
  },
];

const socialIcons = [
  { src: TikTokIcon, alt: 'TikTok' },
  { src: FaceBookIcon, alt: 'Facebook' },
  { src: InstagramIcon, alt: 'Instagram' },
  { src: SnapchatIcon, alt: 'Snapchat' },
  { src: WhatsAppIcon, alt: 'WhatsApp' },
  { src: MessengerIcon, alt: 'Messenger' },
  { src: ShareChatIcon, alt: 'ShareChat' },
  { src: MojIcon, alt: 'Moj' },
  { src: XIcon, alt: 'X' },
];

const prelaunchBenefits = [
  {
    icon: BoltRoundedIcon,
    title: 'Premium features included',
    description: 'Unlimited use of the QuickCam camera app premium features.',
  },
  {
    icon: VerifiedRoundedIcon,
    title: 'Early-member recognition',
    description: 'Get a Founding Member, Beta Tester, or Early Adopter badge.',
  },
  {
    icon: RedeemRoundedIcon,
    title: 'Launch access',
    description:
      'Access QuickCam Utility Coin and Quickie Community Meme Coin launches.',
  },
  {
    icon: GroupsRoundedIcon,
    title: 'Build your network early',
    description:
      'Pre-build your referral network and followers before subscription launch.',
  },
];

const referralPoints = [
  'Refer QuickCam to earn QuickCam Referral Points with Pending status.',
  'When your referrals subscribe, your Referral Points become Confirmed.',
  'The more Confirmed Referral Points you have, the larger your community meme-coin airdrop may be.',
];

const communityCoinBenefits = [
  'Trade or hold them as digital collectible assets.',
  'Use them in the QuickCam ecosystem to unlock perks and exclusive features.',
  'Support creators and friends through the planned crypto-tipping economy.',
];

const ecosystemFeatures = [
  {
    icon: PaymentsRoundedIcon,
    title: 'Creators get paid directly',
    description:
      'Friends, fans, and followers can tip, subscribe, and buy exclusive content.',
  },
  {
    icon: RedeemRoundedIcon,
    title: 'Crypto tipping — coming soon',
    description:
      'Give and receive community meme coins from referrals as tips.',
  },
  {
    icon: AccountBalanceWalletRoundedIcon,
    title: 'QuickCam coin airdrops — coming soon',
    description:
      'Verified members may qualify for Quickie and Speedy community meme coins.',
  },
  {
    icon: PublicRoundedIcon,
    title: 'Passive residual opportunity',
    description:
      'Participate in rewards connected to verified QuickCam referrals.',
  },
  {
    icon: GroupsRoundedIcon,
    title: 'Everyone can participate',
    description: 'QuickCam membership is free during the prelaunch release.',
  },
  {
    icon: WorkspacePremiumRoundedIcon,
    title: 'QuickCam VIPs',
    description:
      'The first 50,000,000 verified members are planned to receive VIP status.',
  },
  {
    icon: EmojiEventsRoundedIcon,
    title: 'Gamification',
    description:
      'Challenges, contests, prizes, badges, and leaderboards bring the community to life.',
  },
];

const vipBenefits = [
  'Free membership',
  'Early access to the QuickCam app in the prelaunch release',
  'Members-only access to Quickie and Speedy Community Meme Coin presales',
  'First 1,000,000 verified members are VIP Founding Members',
  'Next 9,000,000 verified members are VIP Beta Testers',
  'Next 40,000,000 verified members are VIP Early Adopters',
  'VIP badges are planned to convert into VIP NFTs',
  'Grow your referral network and followers',
];

const whyJoinFeatures = [
  {
    title: 'Founding Members',
    description:
      'Founding Member VIP badge for the first 1,000,000 verified members.',
  },
  {
    title: 'Beta Testers',
    description:
      'Beta Tester VIP badge for the next 9,000,000 verified members.',
  },
  {
    title: 'Early Adopters',
    description:
      'Early Adopter VIP badge for the next 40,000,000 verified members.',
  },
  {
    title: 'Referral Points',
    description:
      'Earn Referral Points just for signing up through a verified inviter.',
  },
  {
    title: 'First Wave',
    description:
      'Be part of the first wave of the QuickCam Web3 user experience.',
  },
  {
    title: 'Ground-floor opportunity',
    description:
      'Build an audience early as QuickCam’s creator ecosystem develops.',
  },
  {
    title: 'VIP NFTs',
    description: 'VIP badges are planned to become collectible VIP NFTs.',
  },
  {
    title: 'Community coin airdrop',
    description:
      'Reserved for eligible VIP Founding Members, Beta Testers, and Early Adopters.',
  },
  {
    title: 'QuickCam referral network',
    description: 'Pre-build your followers and connect your inviter network.',
  },
];

const faqs = [
  {
    question: 'How does recording in fast and slow motion in real time work?',
    answer:
      'Touch and hold the record button. While holding, move your finger right to record in fast motion and left to record in slow motion. Lift your finger to see the result immediately in Edit mode.',
  },
  {
    question: 'How much does QuickCam cost to subscribe?',
    answer:
      'QuickCam is free during the current prelaunch period. Subscription pricing after prelaunch will be announced separately.',
  },
  {
    question: 'How can I earn Referral Points?',
    answer:
      'Include your QuickCam referral link when you share. When someone follows it, registers, downloads QuickCam, and verifies their account, the referral can be confirmed toward future community rewards.',
  },
  {
    question: 'What is the QuickCam ecosystem?',
    answer:
      'It is a platform centered on the QuickCam camera app, with social sharing, creator tools, referral features, gamification, and planned community-reward experiences.',
  },
  {
    question: 'What is SocialFi in QuickCam?',
    answer:
      'SocialFi combines social participation with creator-economy features. QuickCam plans to let creators monetize content and receive support through the web dashboard.',
  },
  {
    question: 'What are community meme coins?',
    answer:
      'They are planned digital collectibles intended to support creators and community-driven engagement inside the QuickCam ecosystem.',
  },
  {
    question: 'What is Prelaunch?',
    answer:
      'Prelaunch is an opportunity to download and use QuickCam before the official subscription launch, with early access to its patented camera features.',
  },
  {
    question: 'Is QuickCam really free in Prelaunch?',
    answer:
      'Yes. QuickCam is free during prelaunch so the community can experience the product before subscriptions begin.',
  },
  {
    question: 'Is Prelaunch a Beta test?',
    answer:
      'The current live page describes prelaunch as an official early release of the patented fast- and slow-motion camera experience.',
  },
  {
    question: 'Is everything completely programmed?',
    answer:
      'The current prelaunch experience includes the iPhone and Android apps, referral features, gamification, the referral-network dashboard, and related member experiences.',
  },
  {
    question: 'What still needs to be completed?',
    answer:
      'QuickCam development continues with new features, integrations, and improvements. Product and community announcements identify what is coming next.',
  },
  {
    question: 'When does subscription start?',
    answer:
      'Subscriptions are planned to begin after QuickCam reaches its prelaunch download goals. The official launch date will be announced through QuickCam channels.',
  },
];

const speedCopy: Record<Speed, string> = {
  '0.25x': 'Slow the moment down while recording.',
  '1x': 'Return to natural speed without stopping.',
  '4x': 'Accelerate the action as it happens.',
};

const speedLabels: Record<Speed, string> = {
  '0.25x': 'Slow motion',
  '1x': 'Normal',
  '4x': 'Fast motion',
};

function CinematicHomepage({
  isLoggedIn,
  referredBy,
  isLoadingReferrer = false,
  getReferrerUser,
  onTryQuickCam,
  onLogin,
  onDashboard,
  giftInvite = null,
}: CinematicHomepageProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [patentOpen, setPatentOpen] = useState(false);
  const [availableOpen, setAvailableOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeSpeed, setActiveSpeed] = useState<Speed>('1x');
  const [headerCompact, setHeaderCompact] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const availableCloseRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setLandingAppearanceLock(true);
    return () => setLandingAppearanceLock(false);
  }, []);

  // Cursor Simple Browser can inject a full-viewport iframe that blocks clicks.
  useEffect(() => {
    const neutralizeBlockingIframes = () => {
      document.querySelectorAll('iframe').forEach(iframe => {
        const cs = window.getComputedStyle(iframe);
        const r = iframe.getBoundingClientRect();
        const src = iframe.getAttribute('src') || '';
        const isFullBleed =
          cs.position === 'fixed' &&
          r.width >= window.innerWidth * 0.9 &&
          r.height >= window.innerHeight * 0.9;
        const isCursorOverlay =
          cs.zIndex === '2147483647' ||
          (iframe.getAttribute('style') || '').includes('2147483647');
        if (
          isFullBleed &&
          isCursorOverlay &&
          (!src || src === 'about:blank') &&
          cs.pointerEvents !== 'none'
        ) {
          iframe.style.setProperty('pointer-events', 'none', 'important');
        }
      });
    };

    neutralizeBlockingIframes();
    const overlayObserver = new MutationObserver(neutralizeBlockingIframes);
    overlayObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
    const neutralizeInterval = window.setInterval(
      neutralizeBlockingIframes,
      250,
    );

    return () => {
      overlayObserver.disconnect();
      window.clearInterval(neutralizeInterval);
    };
  }, []);

  useEffect(() => {
    const COMPRESS_THRESHOLD = 16;

    const getScrollTop = () =>
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const updateCompact = () => {
      setHeaderCompact(getScrollTop() > COMPRESS_THRESHOLD);
    };

    updateCompact();
    window.addEventListener('scroll', updateCompact, { passive: true });
    return () => window.removeEventListener('scroll', updateCompact);
  }, []);

  useEffect(() => {
    if (!demoOpen) return undefined;

    closeButtonRef.current?.focus();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDemoOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [demoOpen]);

  useEffect(() => {
    if (!availableOpen) return undefined;

    availableCloseRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAvailableOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [availableOpen]);

  const scrollTo = (target: string) => {
    document.getElementById(target)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setMenuOpen(false);
  };

  const followNavigation = (item: NavigationItem) => {
    if (item.href) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
      setMenuOpen(false);
      return;
    }
    if (item.target) scrollTo(item.target);
  };

  const handlePrimaryAction = () => {
    if (isLoggedIn) {
      onDashboard();
      return;
    }
    onTryQuickCam();
  };

  const closeAvailablePopup = () => setAvailableOpen(false);

  const handleAvailableSignUp = () => {
    setAvailableOpen(false);
    handlePrimaryAction();
  };

  const confirmPrivacyChoice = () => {
    window.localStorage.setItem('isPersonalInformationSell', 'true');
    setPrivacyOpen(false);
  };

  return (
    <main
      className={`${styles.page}${
        headerCompact ? ` ${styles.pageCompact}` : ''
      }`}
    >
      <LandingReferrerWelcome
        isLoadingReferrer={isLoadingReferrer}
        referredBy={referredBy}
        giftInvite={giftInvite}
        getReferrerUser={getReferrerUser || (() => undefined)}
      />
      {createPortal(
        <header
          className={`${styles.header}${
            headerCompact ? ` ${styles.headerCompact}` : ''
          }`}
        >
          <button
            type="button"
            className={styles.brand}
            onClick={() => scrollTo('product')}
            aria-label="QuickCam home"
          >
            <img src={Shape} alt="QuickCam" />
            <span className={styles.brandWordmark}>
              QuickCam<sup>TM</sup>
            </span>
          </button>

          <nav className={styles.desktopNav} aria-label="Primary navigation">
            {navigation.map(item => (
              <button
                type="button"
                key={item.label}
                onClick={() => followNavigation(item)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className={styles.headerActions}>
            {!isLoggedIn && (
              <button
                type="button"
                className={styles.loginButton}
                onClick={onLogin}
              >
                Login
              </button>
            )}
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handlePrimaryAction}
            >
              {isLoggedIn ? 'Open Dashboard' : 'Sign Up'}
            </button>
          </div>

          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setMenuOpen(open => !open)}
            aria-expanded={menuOpen}
            aria-controls="quickcam-mobile-navigation"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
          >
            {menuOpen ? (
              <CloseRoundedIcon />
            ) : (
              <MenuRoundedIcon className={styles.menuIcon} />
            )}
          </button>

          {menuOpen && (
            <nav
              id="quickcam-mobile-navigation"
              className={styles.mobileNav}
              aria-label="Mobile navigation"
            >
              {navigation.map(item => (
                <button
                  type="button"
                  key={item.label}
                  onClick={() => followNavigation(item)}
                >
                  {item.label}
                </button>
              ))}
              {!isLoggedIn && (
                <button
                  type="button"
                  className={styles.mobileLogin}
                  onClick={onLogin}
                >
                  Login
                </button>
              )}
              <button
                type="button"
                className={styles.mobilePrimary}
                onClick={handlePrimaryAction}
              >
                {isLoggedIn ? 'Open Dashboard' : 'Sign Up'}
              </button>
            </nav>
          )}
        </header>,
        document.body,
      )}

      <aside className={styles.announcement} aria-label="QuickCam availability">
        <button
          type="button"
          className={styles.announcementAction}
          onClick={() => setPatentOpen(true)}
        >
          <VerifiedRoundedIcon />
          <span>Patented QuickCam camera app</span>
        </button>
        <button
          type="button"
          className={styles.announcementAction}
          onClick={() => scrollTo('prelaunch')}
        >
          <BoltRoundedIcon />
          <span>Premium free trial in prelaunch</span>
        </button>
        <div className={styles.storeLinks}>
          <button
            type="button"
            className={styles.announcementAction}
            onClick={() => setAvailableOpen(true)}
          >
            <DownloadRoundedIcon />
            Available now
          </button>
          <button
            type="button"
            className={styles.storeBadgeButton}
            onClick={() => setAvailableOpen(true)}
            aria-label="Get QuickCam on Google Play"
          >
            <img src={GoogleBadge} alt="" />
          </button>
          <button
            type="button"
            className={styles.storeBadgeButton}
            onClick={() => setAvailableOpen(true)}
            aria-label="Download QuickCam on the Apple App Store"
          >
            <img src={AppStoreBadge} alt="" />
          </button>
        </div>
      </aside>

      <section className={styles.hero} id="product">
        <div className={styles.heroCopy}>
          <p className={styles.heroBrand}>
            QuickCam<sup>TM</sup>
          </p>
          <h1>
            Capture time
            <span>differently.</span>
          </h1>
          <p className={styles.heroDescription}>
            QuickCam lets you record fast and slow motion while you shoot. Edit,
            finish, and share in one creative flow.
          </p>

          <div className={styles.heroActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handlePrimaryAction}
            >
              {isLoggedIn ? 'Open QuickCam' : 'Sign Up'}
            </button>
            <button
              type="button"
              className={styles.demoButton}
              onClick={() => setDemoOpen(true)}
            >
              <span>
                <PlayArrowRoundedIcon />
              </span>
              Watch 20-second demo
            </button>
          </div>

          <ul className={styles.proofList} aria-label="QuickCam benefits">
            <li>
              <AutoAwesomeRoundedIcon />
              Patented live speed effects
            </li>
            <li>
              <BoltRoundedIcon />
              Quick editing
            </li>
            <li>
              <ShareRoundedIcon />
              One-tap sharing
            </li>
          </ul>
        </div>

        <div
          className={styles.heroVisual}
          aria-label="QuickCam recording BMX action"
        >
          <img
            src={CinematicHero}
            alt="QuickCam recording a BMX rider in motion"
            width="1373"
            height="1146"
            decoding="async"
          />
        </div>
      </section>

      <section className={styles.flowSection} id="how-it-works">
        <div className={styles.flowHeader}>
          <div>
            <p className={styles.eyebrow}>The QuickCam flow</p>
            <h2>One flow. Total control.</h2>
            <p>Record in real time. Shape time in post. Share anywhere.</p>
          </div>

          <ol className={styles.flowSteps}>
            {flowSteps.map(step => (
              <li key={step.number}>
                <span>{step.number}</span>
                <div>
                  <strong>{step.title}</strong>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className={styles.speedPanel}>
          <div className={styles.speedPanelHeader}>
            <div>
              <strong>Live Speed Ramp</strong>
              <span>During recording</span>
            </div>
            <p aria-live="polite">{speedCopy[activeSpeed]}</p>
          </div>

          <div className={styles.speedVisualization}>
            <img
              src={SpeedRamp}
              alt=""
              aria-hidden="true"
              width="1200"
              height="280"
              decoding="async"
            />
            <div className={styles.speedLabels}>
              {(['0.25x', '1x', '4x'] as Speed[]).map(speed => (
                <button
                  type="button"
                  key={speed}
                  className={activeSpeed === speed ? styles.activeSpeed : ''}
                  onClick={() => setActiveSpeed(speed)}
                  aria-pressed={activeSpeed === speed}
                >
                  <span className={styles.speedChip}>
                    <span>{speed}</span>
                    <small>{speedLabels[speed]}</small>
                  </span>
                </button>
              ))}
            </div>
            <div className={styles.speedScale} aria-hidden="true">
              {['-4X', '-3X', '-2X', '1X', '2X', '3X', '4X'].map(mark => (
                <span key={mark}>{mark}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={styles.features} id="features">
        <div className={styles.sectionIntro}>
          <p className={styles.eyebrow}>Main features</p>
          <h2>Everything the real QuickCam experience promises.</h2>
          <p>
            The patented camera, Quickie editor, and social-sharing workflow
            stay together from capture to release.
          </p>
        </div>

        <div className={styles.featureRows}>
          {productFeatures.map(feature => {
            const FeatureIcon = feature.icon;
            return (
              <article key={feature.eyebrow}>
                <p className={styles.featureEyebrow}>{feature.eyebrow}</p>
                <div className={styles.featureHeading}>
                  <div className={styles.featureIcon}>
                    <FeatureIcon />
                  </div>
                  <h3>{feature.title}</h3>
                </div>
                <span>{feature.description}</span>
                <ul>
                  {feature.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {feature.eyebrow === 'Share' && (
                  <div
                    className={styles.socialIcons}
                    aria-label="Supported social platforms"
                  >
                    {socialIcons.map(icon => (
                      <img key={icon.alt} src={icon.src} alt={icon.alt} />
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className={styles.prelaunchSection} id="prelaunch">
        <div className={styles.prelaunchAside}>
          <div className={styles.prelaunchBadge}>
            <img
              src={Prelaunch}
              alt="QuickCam premium free trial prelaunch badge"
            />
          </div>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handlePrimaryAction}
          >
            Get started free
          </button>
        </div>
        <div className={styles.prelaunchContent}>
          <p className={styles.eyebrow}>Prelaunch release</p>
          <h2>No subscription required for the premium free trial.</h2>
          <p className={styles.sectionLead}>
            Explore QuickCam’s premium camera experience now and build your
            place in the community before subscription launch.
          </p>
          <div className={styles.prelaunchGrid}>
            {prelaunchBenefits.map(benefit => {
              const BenefitIcon = benefit.icon;
              return (
                <article key={benefit.title}>
                  <div className={styles.prelaunchHeading}>
                    <BenefitIcon />
                    <h3>{benefit.title}</h3>
                  </div>
                  <p>{benefit.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className={styles.referralSection} id="referral-points">
        <div className={styles.referralCopy}>
          <p className={styles.eyebrow}>Refer. Earn. Convert.</p>
          <h2>Referral Points and community rewards.</h2>
          <p className={styles.sectionLead}>
            Share QuickCam, grow your network, and build eligibility for planned
            Quickie and Speedy community rewards.
          </p>

          <div className={styles.detailColumns}>
            <article>
              <h3>Earn QuickCam Referral Points</h3>
              <ul>
                {referralPoints.map(point => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
            <article>
              <h3>Quickie and Speedy community coins</h3>
              <ul>
                {communityCoinBenefits.map(point => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          </div>
        </div>

        <div className={styles.coinVideo}>
          <video
            muted
            autoPlay
            loop
            playsInline
            aria-label="QuickCam community reward animation"
          >
            <source src={QuickieCoinPlayback} type="video/mp4" />
            <track kind="captions" />
          </video>
          <div>
            <RedeemRoundedIcon />
            <span>Community rewards</span>
          </div>
        </div>
      </section>

      <section className={styles.ecosystemSection} id="ecosystem">
        <div className={styles.sectionIntro}>
          <p className={styles.eyebrow}>The broader QuickCam ecosystem</p>
          <h2>Creator tools, community participation, and planned tipping.</h2>
          <p>
            QuickCam’s camera remains the product at the center; the ecosystem
            layers add ways to participate, build an audience, and support
            creators.
          </p>
        </div>

        <div className={styles.ecosystemGrid}>
          {ecosystemFeatures.map(feature => {
            const EcosystemIcon = feature.icon;
            return (
              <article key={feature.title}>
                <div className={styles.ecosystemHeading}>
                  <EcosystemIcon />
                  <h3>{feature.title}</h3>
                </div>
                <p>{feature.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className={styles.vipSection} id="vip">
        <div className={styles.vipIntro}>
          <div>
            <p className={styles.eyebrow}>Be a QuickCam VIP</p>
            <h2>Founding Members, Beta Testers, and Early Adopters.</h2>
            <ul>
              {vipBenefits.map(benefit => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </div>
          <img
            src={BetaTesterImg}
            alt="QuickCam founding member, beta tester, and early adopter badges"
          />
        </div>

        <div className={styles.whyJoin}>
          <p className={styles.eyebrow}>Why join?</p>
          <div className={styles.whyJoinGrid}>
            {whyJoinFeatures.map((feature, index) => (
              <article key={feature.title}>
                <div className={styles.whyJoinHeading}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{feature.title}</h3>
                </div>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.faqSection} id="faqs">
        <div className={styles.sectionIntro}>
          <p className={styles.eyebrow}>Frequently asked questions</p>
          <h2>
            <span className={styles.faqDesktopTitle}>
              Understand the camera, prelaunch, referrals, and ecosystem.
            </span>
            <span className={styles.faqResponsiveTitle}>
              FAQ - Frequently Asked Questions
            </span>
          </h2>
        </div>

        <div className={styles.faqList}>
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            const answerId = `quickcam-faq-answer-${index}`;
            return (
              <article
                key={faq.question}
                className={isOpen ? styles.openFaq : ''}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                >
                  <span>{faq.question}</span>
                  <ExpandMoreRoundedIcon />
                </button>
                <div id={answerId} hidden={!isOpen}>
                  <p>{faq.answer}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className={styles.creatorStory} id="experience">
        <div className={styles.creatorVideo}>
          <video
            src={SummaryVideo}
            poster={ThumbnailSummary}
            controls
            playsInline
            preload="metadata"
            aria-label="QuickCam camera experience demonstration"
          >
            <track kind="captions" srcLang="en" label="English" />
          </video>
        </div>

        <div className={styles.creatorCopy}>
          <MovieCreationRoundedIcon />
          <p className={styles.eyebrow}>The QuickCam camera experience</p>
          <h2>The patented camera is the heart of the ecosystem.</h2>
          <ul>
            <li>Multi-patented camera app</li>
            <li>Premium free trial in the prelaunch release</li>
            <li>Fast and slow motion while recording</li>
            <li>Quickie editor and social sharing</li>
            <li>Referral network and gamification</li>
            <li>Planned creator tipping and community rewards</li>
          </ul>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handlePrimaryAction}
          >
            Start creating
          </button>
        </div>
      </section>

      <section className={styles.finalCta}>
        <p className={styles.eyebrow}>Premium free trial now in prelaunch</p>
        <h2>Your next moment is already moving.</h2>
        <p>
          Download QuickCam, experience the patented camera, and start building
          your network.
        </p>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handlePrimaryAction}
        >
          {isLoggedIn ? 'Open QuickCam' : 'Get started'}
        </button>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerBrand} aria-label="QuickCam">
          <img src={Shape} alt="" />
          <span className={styles.brandWordmark}>
            QuickCam<sup>TM</sup>
          </span>
        </div>
        <div>
          <a href="/about-us">About us</a>
          <a href="/privacy-policy">Privacy Policy</a>
          <a href="/cookie-policy">Cookie Policy</a>
          <button type="button" onClick={() => setPatentOpen(true)}>
            Patents
          </button>
          <a href="/terms-and-conditions">Terms of Service</a>
          <a href="/trust-policy">Trust Policy</a>
          <button type="button" onClick={() => setPrivacyOpen(true)}>
            Do Not Sell My Personal Information
          </button>
        </div>
        <p>© Copyright 2026 QuickCam, LLC. All rights reserved.</p>
      </footer>

      {demoOpen && (
        <div
          className={styles.demoModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="quickcam-demo-title"
        >
          <div className={styles.demoDialog}>
            <div className={styles.demoDialogHeader}>
              <div>
                <p className={styles.eyebrow}>QuickCam in motion</p>
                <h2 id="quickcam-demo-title">See the creative flow.</h2>
              </div>
              <button
                type="button"
                ref={closeButtonRef}
                onClick={() => setDemoOpen(false)}
                aria-label="Close demonstration"
              >
                <CloseRoundedIcon />
              </button>
            </div>
            <video src={LandingIntroVideo} controls autoPlay playsInline>
              <track kind="captions" />
            </video>
          </div>
        </div>
      )}

      {patentOpen && (
        <div
          className={styles.demoModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="patent-title"
        >
          <div className={styles.policyDialog}>
            <div className={styles.demoDialogHeader}>
              <h2 id="patent-title">QuickCam patents</h2>
              <button
                type="button"
                onClick={() => setPatentOpen(false)}
                aria-label="Close patents"
              >
                <CloseRoundedIcon />
              </button>
            </div>
            <img src={PatentPopup} alt="QuickCam patent information" />
          </div>
        </div>
      )}

      {availableOpen &&
        createPortal(
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <div
            className={styles.availableModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="available-title"
            onClick={closeAvailablePopup}
          >
            <div
              className={styles.availableDialog}
              onClick={event => event.stopPropagation()}
            >
              <div className={styles.demoDialogHeader}>
                <div>
                  <p className={styles.eyebrow}>Available now</p>
                  <h2 id="available-title">
                    QuickCam is ready on iPhone and Android.
                  </h2>
                </div>
                <button
                  type="button"
                  ref={availableCloseRef}
                  onClick={closeAvailablePopup}
                  aria-label="Close availability"
                >
                  <CloseRoundedIcon />
                </button>
              </div>
              <p>
                Download QuickCam today for iPhone and Android phones. Create
                your free account to unlock the premium free trial in prelaunch
                and start capturing fast and slow motion while you shoot.
              </p>
              <ol className={styles.availableSteps}>
                <li>Tap Sign Up to create your QuickCam account.</li>
                <li>
                  Complete signup, then install QuickCam from the App Store or
                  Google Play.
                </li>
                <li>Open the app, sign in, and start creating.</li>
              </ol>
              <div className={styles.availableStores} aria-hidden="true">
                <img src={GoogleBadge} alt="" />
                <img src={AppStoreBadge} alt="" />
              </div>
              <div className={styles.dialogActions}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={closeAvailablePopup}
                >
                  Close
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleAvailableSignUp}
                >
                  {isLoggedIn ? 'Open Dashboard' : 'Sign Up'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {privacyOpen && (
        <div
          className={styles.demoModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-title"
        >
          <div className={styles.privacyDialog}>
            <div className={styles.demoDialogHeader}>
              <h2 id="privacy-title">Do Not Sell My Personal Information</h2>
              <button
                type="button"
                onClick={() => setPrivacyOpen(false)}
                aria-label="Close privacy choice"
              >
                <CloseRoundedIcon />
              </button>
            </div>
            <p>
              Confirm that you want QuickCam to record this opt-out preference
              on this device.
            </p>
            <div className={styles.dialogActions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setPrivacyOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={confirmPrivacyChoice}
              >
                Confirm opt-out
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

CinematicHomepage.defaultProps = {
  referredBy: undefined,
  isLoadingReferrer: false,
  getReferrerUser: undefined,
  giftInvite: null,
};

export default CinematicHomepage;
